import { Component, Show } from "solid-js";
import { A } from "@solidjs/router";
import type { Project } from "../../api/types";

/**
 * FLOW-889: says why the selected project cannot be written to. `read_only` is
 * derived server-side and has two causes that need different advice — the
 * tenant is over its plan's active-project cap, or the project is archived.
 * Upgrading does not un-archive anything, so the archived variant points at
 * Settings and drops the plans link rather than offering contradictory advice.
 *
 * The banner informs; it does not disable anything. Per-control write gating
 * was rejected (D8) and the API remains the enforcement point.
 */
export const ProjectReadOnlyBanner: Component<{
  project: Project | undefined;
  isTenantAdmin: boolean;
}> = (props) => {
  const archived = () => props.project?.archived_at != null;

  return (
    <Show when={props.project?.read_only}>
      <div
        role="status"
        aria-live="polite"
        data-testid="project-read-only-banner"
        data-reason={archived() ? "archived" : "cap"}
        class="border-b border-signal-amber/25 bg-signal-amber-tint px-4 py-2 flex items-start gap-3"
      >
        <p class="flex-1 min-w-0 text-sm text-signal-amber">
          <Show
            when={archived()}
            fallback="This project is read-only — the Personal plan includes 3 active projects. Archive a project or upgrade to Pro."
          >
            This project is archived. Unarchive it from Settings to edit.
          </Show>
        </p>
        <Show when={!archived() && props.isTenantAdmin}>
          <A
            href="/settings/billing"
            class="shrink-0 text-sm font-medium text-signal-amber underline hover:no-underline rounded-sm focus-ring"
          >
            View plans
          </A>
        </Show>
      </div>
    </Show>
  );
};
